import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiCamera, FiCheck, FiAlertCircle, FiImage } from 'react-icons/fi';
import { FormAlert } from './form';
import { useApi } from '../hooks/useApi';
import { preprocessImageForOcr } from '../utils/ocrImagePreprocess';
import { OCR_SCAN_BETA_MESSAGE } from '../constants/ocrBeta';

const STEPS = [
  { id: 1, label: 'Escaneo', hint: 'Subí o escaneá el comprobante' },
  { id: 2, label: 'Revisá', hint: 'Corregí monto, fecha o concepto si hace falta' },
  { id: 3, label: 'Guardá', hint: 'Confirmá y registrá el gasto' },
];

export default function ExpenseOcrCapture({
  modoOscuro,
  onApply,
  onCancel,
  usage: initialUsage,
  embedded = false,
  onUsageChange,
  betaPaused = false,
}) {
  const { secureFetch } = useApi();
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [provider, setProvider] = useState(null);
  const [usage, setUsage] = useState(initialUsage || null);

  const providerLabel = provider === 'google_vision'
    ? 'Google Vision'
    : provider === 'tesseract_client'
      ? 'Lector local'
      : null;

  useEffect(() => {
    if (initialUsage) setUsage(initialUsage);
  }, [initialUsage]);

  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const box = embedded
    ? ''
    : modoOscuro ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white';
  const inputClass = modoOscuro ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300';
  const stepBg = modoOscuro ? 'bg-gray-800/80 border-gray-700' : 'bg-gray-50 border-gray-200';

  const currentStep = !preview ? 1 : parsed ? 2 : 1;
  const noScansLeft = !betaPaused && usage?.remaining === 0;
  const scanBlocked = betaPaused || noScansLeft;

  const usageHint = usage
    ? usage.trialOnly
      ? `${usage.trialRemaining ?? 0} prueba(s) restante(s)`
      : `${usage.remaining ?? 0} de ${usage.effectiveLimit} escaneos este mes`
    : null;

  const updateUsage = (next) => {
    setUsage(next);
    onUsageChange?.(next);
  };

  const fieldWarnClass = (field) => {
    const c = confidence?.[field];
    if (c == null || c >= 0.55) return '';
    return modoOscuro ? 'border-amber-500 ring-1 ring-amber-500/40' : 'border-amber-400 ring-1 ring-amber-300/60';
  };

  const applyParseResult = (data) => {
    const fields = {
      concepto: data.fields?.concepto || '',
      monto: data.fields?.monto ?? null,
      fecha: data.fields?.fecha || new Date().toISOString().slice(0, 10),
      proveedor: data.fields?.proveedor || '',
    };
    setParsed(fields);
    setConfidence(data.confidence || null);
    setWarnings(Array.isArray(data.warnings) ? data.warnings : []);
    setProvider(data.provider || null);
    if (data.usage) updateUsage(data.usage);

    if (data.provider === 'google_vision' && !data.needsReview && fields.concepto && fields.monto) {
      setError('');
      return;
    }
    if (!fields.monto) {
      setError('No detectamos un monto claro. Completá el importe manualmente.');
    } else if (data.needsReview || !fields.concepto) {
      setError('');
    }
  };

  const handleFile = async (e) => {
    if (betaPaused) {
      setError(OCR_SCAN_BETA_MESSAGE);
      if (e.target) e.target.value = '';
      return;
    }
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setParsed(null);
    setConfidence(null);
    setWarnings([]);
    setProvider(null);
    setLoading(true);
    setPreview(URL.createObjectURL(file));
    try {
      const processed = await preprocessImageForOcr(file);
      const formData = new FormData();
      formData.append('image', processed);
      const extractRes = await secureFetch('/api/ocr/extract', { method: 'POST', body: formData });

      if (extractRes.ok) {
        const data = await extractRes.json();
        applyParseResult(data);
        return;
      }

      const extractData = await extractRes.json().catch(() => ({}));
      if (extractData.usage) updateUsage(extractData.usage);

      if (extractData.code === 'OCR_BETA_PAUSED') {
        setError(extractData.message || OCR_SCAN_BETA_MESSAGE);
        return;
      }

      if (extractRes.status === 402 || extractRes.status === 403) {
        throw new Error(extractData.message || 'Sin cupo de escaneos disponible.');
      }

      if (extractRes.status === 503) {
        const { createWorker } = await import('tesseract.js');
        const worker = await createWorker('spa');
        await worker.setParameters({
          tessedit_pageseg_mode: '6',
          preserve_interword_spaces: '1',
        });
        const { data: ocrData } = await worker.recognize(processed);
        await worker.terminate();

        const parseRes = await secureFetch('/api/ocr/parse-text', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: ocrData.text, provider: 'tesseract_client' }),
        });
        const parseData = await parseRes.json();
        if (parseData.code === 'OCR_BETA_PAUSED') {
          setError(parseData.message || OCR_SCAN_BETA_MESSAGE);
          return;
        }
        if (!parseRes.ok) throw new Error(parseData.message || 'No se pudo registrar el escaneo.');

        applyParseResult(parseData);
        return;
      }

      throw new Error(extractData.message || 'No se pudo leer el comprobante.');
    } catch (err) {
      setError(err.message || 'No se pudo leer el ticket.');
      setParsed({ concepto: '', monto: null, fecha: new Date().toISOString().slice(0, 10), proveedor: '' });
      setConfidence(null);
      setWarnings(['No pudimos leer el comprobante. Completá los campos manualmente.']);
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleReset = () => {
    setPreview(null);
    setParsed(null);
    setConfidence(null);
    setWarnings([]);
    setProvider(null);
    setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleApply = () => {
    if (!parsed?.concepto?.trim() || !parsed?.monto) return;
    onApply?.({
      concepto: parsed.concepto.trim(),
      monto: Number(parsed.monto),
      fecha: parsed.fecha,
      proveedor: parsed.proveedor || '',
      moneda: 'ARS',
      categoria: 'Otro',
    });
  };

  return (
    <div className={`${embedded ? 'space-y-4' : `rounded-2xl border p-4 sm:p-6 space-y-4 ${box}`}`}>
      {!embedded && (
        <div>
          <h2 className={`text-lg font-semibold ${textPri}`}>Escanear comprobante</h2>
          <p className={`text-xs mt-1 ${textMuted}`}>
            Escaneá la factura o ticket. Detectamos monto, fecha y concepto para que confirmes antes de guardar.
          </p>
        </div>
      )}

      <ol className="grid grid-cols-3 gap-2">
        {STEPS.map((step) => {
          const active = currentStep === step.id;
          const done = currentStep > step.id;
          return (
            <li
              key={step.id}
              className={`rounded-xl border px-2.5 py-2 text-center ${stepBg} ${
                active ? (modoOscuro ? 'border-[#1F80FF] ring-1 ring-[#1F80FF]/40' : 'border-[#1F80FF] ring-1 ring-[#1F80FF]/25') : ''
              }`}
            >
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold mb-1 ${
                done || active ? 'bg-[#1F80FF] text-white' : modoOscuro ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
              }`}>
                {done ? '✓' : step.id}
              </span>
              <p className={`text-[11px] font-semibold ${active ? textPri : textMuted}`}>{step.label}</p>
            </li>
          );
        })}
      </ol>

      {betaPaused && (
        <div className={`flex items-start gap-2 rounded-xl border px-3 py-2.5 text-xs ${modoOscuro ? 'border-amber-800 bg-amber-950/30 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
          <FiAlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            <strong className="font-semibold">Próximamente en la beta.</strong> {OCR_SCAN_BETA_MESSAGE}
          </span>
        </div>
      )}

      {usageHint && !betaPaused && (
        <p className={`text-xs font-medium ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
          {usageHint}
          {noScansLeft && (
            <Link to="/uso-plan#ocr-packs" className="ml-1 underline">Comprar escaneos</Link>
          )}
        </p>
      )}

      <FormAlert type="error">{error}</FormAlert>

      {warnings.length > 0 && !error && (
        <div className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-xs ${modoOscuro ? 'border-amber-800 bg-amber-950/30 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
          <FiAlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <ul className="space-y-1">
            {warnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        </div>
      )}

      {noScansLeft && (
        <div className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-xs ${modoOscuro ? 'border-amber-800 bg-amber-950/30 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
          <FiAlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            Sin escaneos disponibles.{' '}
            <Link to="/uso-plan#ocr-packs" className="font-semibold underline">Ver packs</Link>
            {' '}o cargá el gasto manualmente abajo.
          </span>
        </div>
      )}

      {!preview && betaPaused && (
        <div
          className={`flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-4 py-8 ${
            modoOscuro ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'
          } opacity-90`}
          aria-disabled="true"
        >
          <FiCamera className={`w-8 h-8 ${textMuted}`} />
          <p className={`text-sm font-semibold text-center ${textPri}`}>Escanear — próximamente</p>
          <p className={`text-xs text-center max-w-sm ${textMuted}`}>Durante la beta usá carga manual abajo.</p>
        </div>
      )}

      {!preview && !scanBlocked && (
        <label
          className={`flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-4 py-8 cursor-pointer transition-colors ${
            modoOscuro
              ? 'border-gray-600 hover:border-[#1F80FF] hover:bg-gray-800/50'
              : 'border-gray-300 hover:border-[#1F80FF] hover:bg-blue-50/40'
          } ${loading ? 'opacity-60 pointer-events-none' : ''}`}
        >
          <span className={`w-14 h-14 rounded-2xl flex items-center justify-center ${modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#004AAD]'}`}>
            {loading ? <FiImage className="w-7 h-7 animate-pulse" /> : <FiCamera className="w-7 h-7" />}
          </span>
          <div className="text-center">
            <p className={`text-sm font-semibold ${textPri}`}>
              {loading ? 'Leyendo imagen…' : 'Tocá para escanear o subir un comprobante'}
            </p>
            <p className={`text-xs mt-1 max-w-xs ${textMuted}`}>
              Factura, ticket o recibo. Buena luz y que se vea el total.
            </p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleFile}
          />
        </label>
      )}

      {preview && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-start gap-3">
            <img
              src={preview}
              alt="Vista previa del comprobante"
              className="max-h-40 rounded-xl border object-contain shrink-0"
            />
            <div className="flex-1 min-w-[140px] space-y-2">
              <p className={`text-xs ${textMuted}`}>
                {loading ? 'Estamos leyendo el comprobante…' : 'Revisá los datos detectados. Podés corregirlos antes de continuar.'}
              </p>
              {!loading && (
                <button
                  type="button"
                  onClick={handleReset}
                  className={`text-xs font-medium underline ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}
                >
                  Escanear otro comprobante
                </button>
              )}
            </div>
          </div>

          {parsed && !loading && (
            <div className="space-y-3">
              {providerLabel && (
                <p className={`text-[11px] font-medium ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                  Lectura: {providerLabel}
                  {provider === 'tesseract_client' && ' (calidad limitada en tickets difíciles)'}
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className={`block text-xs mb-1 ${textMuted}`}>
                    ¿En qué gastaste? {confidence?.concepto < 0.55 && <span className="text-amber-500">· revisar</span>}
                  </label>
                  <input
                    type="text"
                    value={parsed.concepto}
                    onChange={(e) => setParsed((p) => ({ ...p, concepto: e.target.value }))}
                    placeholder="Ej. Supermercado, nafta, insumos…"
                    className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass} ${fieldWarnClass('concepto')}`}
                  />
                </div>
                <div>
                  <label className={`block text-xs mb-1 ${textMuted}`}>
                    Monto total {confidence?.monto < 0.55 && <span className="text-amber-500">· revisar</span>}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={parsed.monto ?? ''}
                    onChange={(e) => setParsed((p) => ({ ...p, monto: e.target.value }))}
                    placeholder="0,00"
                    className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass} ${fieldWarnClass('monto')}`}
                  />
                </div>
                <div>
                  <label className={`block text-xs mb-1 ${textMuted}`}>
                    Fecha del gasto {confidence?.fecha < 0.55 && <span className="text-amber-500">· revisar</span>}
                  </label>
                  <input
                    type="date"
                    value={parsed.fecha}
                    onChange={(e) => setParsed((p) => ({ ...p, fecha: e.target.value }))}
                    className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass} ${fieldWarnClass('fecha')}`}
                  />
                </div>
              </div>
              <button
                type="button"
                disabled={!parsed.concepto || !parsed.monto}
                onClick={handleApply}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1F80FF] text-white font-semibold text-sm disabled:opacity-50"
              >
                <FiCheck className="w-4 h-4" />
                Continuar y completar el gasto
              </button>
            </div>
          )}
        </div>
      )}

      {!embedded && onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className={`px-3 py-2 rounded-xl text-sm border ${modoOscuro ? 'border-gray-600' : 'border-gray-200'}`}
        >
          Volver al listado
        </button>
      )}

      {!preview && !scanBlocked && (
        <div className={`rounded-xl px-3 py-2.5 text-[11px] leading-relaxed ${stepBg} border`}>
          <p className={`font-semibold mb-1 ${textPri}`}>Consejos para mejores resultados</p>
          <ul className={`space-y-0.5 ${textMuted}`}>
            <li>· Aplaná el ticket y escanealo de frente, con buena luz</li>
            <li>· Que se vea claro el TOTAL y la fecha</li>
            <li>· Si la lectura falla, completá vos — no confíes en nombres raros</li>
          </ul>
        </div>
      )}
    </div>
  );
}
